'use client';

import { useTranslations } from 'next-intl';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import ProgramDetailDatesModal from '@/app/[locale]/[domain]/(default-layout)/program/[contentId]/_components/ProgramDetailDatesModal';
import ProgramDetailDateTabs from '@/app/[locale]/[domain]/(default-layout)/program/[contentId]/_components/ProgramDetailDateTabs';
import EventListPlaceholder from '@/app/[locale]/[domain]/(default-layout)/program/_components/EventListPlaceholder';
import ProgramFilter from '@/app/[locale]/[domain]/(default-layout)/program/_components/filter/ProgramFilter';
import TicketButtonGrouped from '@/app/[locale]/[domain]/(default-layout)/program/_components/TicketButtonGrouped';

import { ShowingDto } from '@cineamo/api/models/showing/ShowingDto.types';

import { calendarDayKeyInZone } from '@cineamo/ui/src/helpers/cinema-day';
import { getPageBackgroundStyle } from '@cineamo/ui/src/helpers/website-style';

import { CachedContentData } from '@/hooks/useCachedContentData';
import useScrollSpy from '@/hooks/useScrollSpy';
import useShowingsListFiltered from '@/hooks/useShowingsListFiltered';
import { useFormat } from '@cineamo/ui/src/hooks/formatter/useFormat';

import { IconCircleCrossed24 } from '@cineamo/ui/src/icons/ic-circleCrossed';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

type ProgramDetailScreeningsProps = {
    content: CachedContentData;
    /** Day key (`yyyy-MM-dd`) from the `?date=` query to scroll to on arrival. */
    selectedDate?: string;
    initialShowings: ShowingDto[];
    initialShowingsUpdatedAt: number;
};

type DayGroup = {
    key: string;
    showings: ShowingDto[];
};

/** `id` of the sticky `CineamoAppBanner` link. */
const APP_BANNER_ID = '__cineamo_app_banner__';

const dayElementId = (key: string) => `program-detail-day-${key}`;

/**
 * All upcoming screenings of the content as one list grouped by date, under a sticky "Screenings"
 * header whose date tabs follow the scroll position and jump to a date on click.
 */
export default function ProgramDetailScreenings(props: ProgramDetailScreeningsProps): React.JSX.Element {
    const { content, selectedDate, initialShowings, initialShowingsUpdatedAt } = props;
    const t = useTranslations();
    const { timeZone, style } = useDomainConfiguration();
    const format = useFormat({ timeZone });

    const { showings, noShowingsForSelectedCinemas } = useShowingsListFiltered({
        contentId: content.id,
        selectedDate: 'all',
        initialShowings,
        initialShowingsUpdatedAt
    });

    // Server render and hydration filter against the same instant; the effect then keeps it current
    const [now, setNow] = useState(initialShowingsUpdatedAt);
    useEffect(() => {
        setNow(Date.now());
    }, []);
    const upcoming = useMemo(
        () => showings.filter((showing) => new Date(showing.startDatetime).getTime() > now),
        [showings, now]
    );

    const groups = useMemo(() => {
        const byDay = new Map<string, ShowingDto[]>();
        for (const showing of [...upcoming].sort(
            (a, b) => new Date(a.startDatetime).getTime() - new Date(b.startDatetime).getTime()
        )) {
            const key = calendarDayKeyInZone(showing.startDatetime, timeZone);
            byDay.set(key, [...(byDay.get(key) ?? []), showing]);
        }
        return Array.from(byDay, ([key, dayShowings]): DayGroup => ({ key, showings: dayShowings }));
    }, [upcoming, timeZone]);

    const headerRef = useRef<HTMLDivElement>(null);
    // Where the pinned header ends: its sticky `top` (the app banner on mobile) plus its own height
    const getHeaderHeight = useCallback(() => {
        const header = headerRef.current;
        return header ? (Number.parseFloat(getComputedStyle(header).top) || 0) + header.offsetHeight : 0;
    }, []);
    const groupElementIds = useMemo(() => groups.map((group) => dayElementId(group.key)), [groups]);
    const getHeaderBottom = useCallback(() => headerRef.current?.getBoundingClientRect().bottom ?? 0, []);
    const { activeId, scrollTo } = useScrollSpy(groupElementIds, getHeaderHeight, getHeaderBottom);
    const activeKey = groups.find((group) => dayElementId(group.key) === activeId)?.key ?? null;

    // Mobile: the sticky app banner sits at top 0 too, so the header pins below it instead of under it.
    // It is display:none from lg on (height 0) and can be switched off per cinema, hence measured.
    const [stickyTop, setStickyTop] = useState(0);
    useEffect(() => {
        const banner = document.getElementById(APP_BANNER_ID);
        if (!banner) {
            return;
        }
        const update = () => setStickyTop(banner.offsetHeight);
        update();
        const observer = new ResizeObserver(update);
        observer.observe(banner);
        return () => observer.disconnect();
    }, []);

    // Mobile: the floating filter button must not sit on the not-yet-pinned tab bar
    const [hideFab, setHideFab] = useState(false);
    useEffect(() => {
        const update = () => {
            const rect = headerRef.current?.getBoundingClientRect();
            const bannerHeight = document.getElementById(APP_BANNER_ID)?.offsetHeight ?? 0;
            setHideFab(!!rect && rect.top > bannerHeight && rect.bottom > window.innerHeight - 140);
        };
        update();
        window.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update);
        // The header moves without scrolling when the details above expand or collapse
        const observer = new ResizeObserver(update);
        observer.observe(document.body);
        return () => {
            window.removeEventListener('scroll', update);
            window.removeEventListener('resize', update);
            observer.disconnect();
        };
    }, []);

    const [isCalendarOpen, setIsCalendarOpen] = useState(false);

    function selectDay(key: string | null) {
        scrollTo(key === null ? null : dayElementId(key));
    }

    // Deep link from the program list (`?date=`): start on that day's group
    useEffect(() => {
        if (selectedDate && groups.some((group) => group.key === selectedDate)) {
            scrollTo(dayElementId(selectedDate));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- arrival only, not on every list refresh
    }, []);

    // Noon UTC names the same calendar day in every zone the cinemas run in
    const dayLabels = (key: string) => {
        const instant = new Date(`${key}T12:00:00Z`);
        const isToday = key === calendarDayKeyInZone(new Date(now), timeZone);
        return {
            weekday: isToday
                ? t('program.today')
                : format.dateTime.formatDate(instant, {
                      weekday: 'long',
                      year: undefined,
                      month: undefined,
                      day: undefined
                  }),
            weekdayShort: isToday ? t('program.today') : format.dateTime.formatWeekday(instant).replace('.', ''),
            // Far-future dates (the list is unbounded) are ambiguous without a year
            dayMonth:
                key.slice(0, 4) === calendarDayKeyInZone(new Date(now), timeZone).slice(0, 4)
                    ? format.dateTime.formatDayMonth(instant)
                    : format.dateTime.formatDate(instant, { day: '2-digit', month: '2-digit', year: '2-digit' })
        };
    };

    const tabs = [
        { key: null, label: t('common.all'), count: upcoming.length },
        ...groups.map((group) => {
            const labels = dayLabels(group.key);
            return {
                key: group.key,
                label: labels.weekdayShort,
                sublabel: labels.dayMonth,
                count: group.showings.length
            };
        })
    ];

    return (
        <div>
            <div
                ref={headerRef}
                className="sticky top-0 z-20 flex flex-col gap-12 lg:gap-16 pt-16 pb-8 lg:-mt-16 bg-[var(--background-color)]"
                // The page background is a viewport-fixed gradient (see `getPageBackgroundStyle`). A flat
                // `--background-color` alone showed up as a lighter box; attaching the same gradient to the
                // viewport makes the pinned header indistinguishable from the page behind it.
                style={{ ...getPageBackgroundStyle(style), backgroundAttachment: 'fixed', top: stickyTop }}>
                <div className="flex items-center justify-between h-26 lg:h-28">
                    <h2 className="text-headline-20 leading-[26px] lg:leading-[28px] tracking-[1px]">
                        {t('program-detail.screenings')}
                    </h2>
                    <div className="lg:-my-10">
                        <ProgramFilter
                            hideFloatingButton={hideFab}
                            showSorting={false}
                        />
                    </div>
                </div>
                <ProgramDetailDateTabs
                    tabs={tabs}
                    activeKey={activeKey}
                    onSelect={selectDay}
                    onOpenCalendar={groups.length > 0 ? () => setIsCalendarOpen(true) : undefined}
                />
                {/* The list fades out under the pinned header instead of being cut off */}
                <div
                    className="absolute inset-x-0 top-full h-16 pointer-events-none bg-[var(--background-color)] [mask-image:linear-gradient(to_bottom,black,transparent)]"
                    style={{ ...getPageBackgroundStyle(style), backgroundAttachment: 'fixed' }}
                />
            </div>

            {noShowingsForSelectedCinemas && groups.length > 0 && (
                <div className="h-[96px] flex items-center justify-center opacity-20">
                    <IconCircleCrossed24 />
                </div>
            )}

            {groups.length === 0 && <EventListPlaceholder />}

            <div className="mt-16">
                {groups.map((group) => {
                    const labels = dayLabels(group.key);
                    return (
                        <section
                            key={group.key}
                            id={dayElementId(group.key)}
                            className="flex flex-col gap-12 py-16 lg:last:min-h-[calc(100dvh-132px)]">
                            <div className="flex flex-row items-center justify-between h-26">
                                <h3 className="flex flex-row gap-8 text-body-16 leading-[26px] tracking-[0.8px]">
                                    <span className="text-body-bold-16">{labels.weekday}</span>
                                    <span>{labels.dayMonth}</span>
                                </h3>
                                <span className="text-body-12 leading-[20px] tracking-[0.2px] opacity-70">
                                    {t('program-detail.screenings-count', { count: group.showings.length })}
                                </span>
                            </div>
                            <div className="grid grid-cols-2 gap-12 lg:grid-cols-[repeat(auto-fill,180px)]">
                                {group.showings.map((showing) => (
                                    <TicketButtonGrouped
                                        key={showing.id}
                                        className="h-[134px] text-[var(--on-surface-color)]"
                                        variant="detail"
                                        showing={showing}
                                        content={content}
                                        showTags
                                    />
                                ))}
                            </div>
                        </section>
                    );
                })}
            </div>

            <ProgramDetailDatesModal
                isOpen={isCalendarOpen}
                dates={groups.map((group) => {
                    const l = dayLabels(group.key);
                    return {
                        key: group.key,
                        label: `${l.weekdayShort}, ${l.dayMonth}`,
                        count: group.showings.length
                    };
                })}
                onClose={() => setIsCalendarOpen(false)}
                onSelect={(key) => {
                    setIsCalendarOpen(false);
                    selectDay(key);
                }}
            />
        </div>
    );
}
